import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchAllReviews,
    moderateReview,
    adminDeleteReview, // ✅ NEW
    clearError,
    clearSuccess,
} from '../redux/slices/reviewSlice';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const PreviewReviewModal = ({ review, onClose }) => {
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-custom-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-bold text-custom-blue">Review Details</h2>
                    <button onClick={onClose} className="text-gray-600 hover:text-custom-blue">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div className="space-y-4">
                    <p><strong className="text-custom-blue">User:</strong> {review.user}</p>
                    <p><strong className="text-custom-blue">Package:</strong> {review.package}</p>
                    <p><strong className="text-custom-blue">Tour Type:</strong> {review.tourType}</p>
                    <p><strong className="text-custom-blue">Rating:</strong> {review.rating}/5</p>
                    <p><strong className="text-custom-blue">Comment:</strong> {review.comment}</p>
                    <p><strong className="text-custom-blue">Status:</strong> {review.status}</p>
                    {review.media.length > 0 && (
                        <div>
                            <strong className="text-custom-blue">Media:</strong>
                            <div className="grid grid-cols-2 gap-4 mt-2">
                                {review.media.map((item, index) => (
                                    <div key={index}>
                                        {item.type === 'image' ? (
                                            <img src={item.url} alt={`Media ${index + 1}`} className="w-full h-32 object-cover rounded-lg" />
                                        ) : (
                                            <video src={item.url} className="w-full h-32 object-cover rounded-lg" controls />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const AdminReviewManagement = () => {
    const dispatch = useDispatch();
    const { reviews, loading, error, success, total, pages, currentPage } = useSelector((state) => state.reviews);
    const [filter, setFilter] = useState('');
    const [page, setPage] = useState(1);
    const [previewReview, setPreviewReview] = useState(null);

    useEffect(() => {
        dispatch(fetchAllReviews({ status: filter, page, limit: 10 }));
    }, [dispatch, filter, page]);

    useEffect(() => {
        if (error) {
            toast.error(error);
            dispatch(clearError());
        }
        if (success) {
            toast.success('Action completed successfully');
            dispatch(clearSuccess());
        }
    }, [error, success, dispatch]);

    const handleModerate = async (id, status) => {
        try {
            await dispatch(moderateReview({ id, status })).unwrap();
            dispatch(fetchAllReviews({ status: filter, page, limit: 10 }));
        } catch (err) {
            toast.error('Failed to moderate review');
        }
    };

    // ✅ NEW: Admin delete review handler
    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this review?')) return;
        try {
            await dispatch(adminDeleteReview(id)).unwrap();
            toast.success('Review deleted successfully');
            dispatch(fetchAllReviews({ status: filter, page, limit: 10 }));
        } catch (err) {
            toast.error('Failed to delete review');
        }
    };

    const sortedReviews = [...reviews].sort((a, b) => {
        const statusOrder = { Pending: 1, Approved: 2, Rejected: 3 };
        return statusOrder[a.status] - statusOrder[b.status];
    });

    return (
        <div className="p-6 max-w-7xl mx-auto bg-custom-white">
            <h1 className="text-3xl font-bold text-custom-blue mb-6">Review Management</h1>
            {loading && <div className="text-custom-blue">Loading reviews...</div>}
            <div className="mb-4">
                <label className="text-custom-blue mr-2">Filter by Status:</label>
                <select
                    value={filter}
                    onChange={(e) => {
                        setFilter(e.target.value);
                        setPage(1);
                    }}
                    className="p-2 border border-gray-300 rounded-lg focus:ring-custom-blue focus:border-custom-blue"
                >
                    <option value="">All</option>
                    <option value="Pending">Pending</option>
                    <option value="Approved">Approved</option>
                    <option value="Rejected">Rejected</option>
                </select>
            </div>
            <div className="overflow-x-auto">
                <table className="min-w-full bg-custom-white border border-gray-300">
                    <thead>
                        <tr className="bg-custom-blue text-custom-white">
                            <th className="py-2 px-4 text-left">User</th>
                            <th className="py-2 px-4 text-left">Package</th>
                            <th className="py-2 px-4 text-left">Rating</th>
                            <th className="py-2 px-4 text-left">Status</th>
                            <th className="py-2 px-4 text-left">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedReviews.map((review) => (
                            <tr key={review._id} className="border-t border-gray-300">
                                <td className="py-2 px-4 text-custom-blue">{review.user}</td>
                                <td className="py-2 px-4 text-custom-blue">{review.package}</td>
                                <td className="py-2 px-4 text-custom-blue">{review.rating}/5</td>
                                <td className="py-2 px-4">
                                    <span
                                        className={`px-2 py-1 rounded-full text-sm ${review.status === 'Pending'
                                            ? 'bg-yellow-100 text-yellow-700'
                                            : review.status === 'Approved'
                                                ? 'bg-green-100 text-green-700'
                                                : 'bg-red-100 text-red-700'
                                            }`}
                                    >
                                        {review.status}
                                    </span>
                                </td>
                                <td className="py-2 px-4 flex space-x-2">
                                    <button
                                        onClick={() => setPreviewReview(review)}
                                        className="px-2 py-1 bg-custom-blue text-custom-white rounded hover:bg-custom-blue/90"
                                    >
                                        Preview
                                    </button>
                                    {review.status === 'Pending' && (
                                        <>
                                            <button
                                                onClick={() => handleModerate(review._id, 'Approved')}
                                                className="px-2 py-1 bg-green-500 text-custom-white rounded hover:bg-green-600"
                                            >
                                                Approve
                                            </button>
                                            <button
                                                onClick={() => handleModerate(review._id, 'Rejected')}
                                                className="px-2 py-1 bg-red-500 text-custom-white rounded hover:bg-red-600"
                                            >
                                                Reject
                                            </button>
                                        </>
                                    )}
                                    {/* ✅ NEW: Delete Button */}
                                    <button
                                        onClick={() => handleDelete(review._id)}
                                        className="px-2 py-1 bg-red-700 text-custom-white rounded hover:bg-red-800"
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="mt-4 flex justify-between items-center">
                <span className="text-custom-blue">
                    Showing {(page - 1) * 10 + 1} to {Math.min(page * 10, total)} of {total} reviews
                </span>
                <div className="space-x-2">
                    <button
                        onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                        disabled={page === 1}
                        className={`px-4 py-2 rounded-lg ${page === 1 ? 'bg-gray-300 cursor-not-allowed' : 'bg-custom-blue text-custom-white hover:bg-custom-blue/90'}`}
                    >
                        Previous
                    </button>
                    <button
                        onClick={() => setPage((prev) => Math.min(prev + 1, pages))}
                        disabled={page === pages}
                        className={`px-4 py-2 rounded-lg ${page === pages ? 'bg-gray-300 cursor-not-allowed' : 'bg-custom-blue text-custom-white hover:bg-custom-blue/90'}`}
                    >
                        Next
                    </button>
                </div>
            </div>
            {previewReview && <PreviewReviewModal review={previewReview} onClose={() => setPreviewReview(null)} />}
        </div>
    );
};

export default AdminReviewManagement;